import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import "./InfoTooltip.css";

// `text`      — tooltip content.
// `children`  — optional custom trigger; defaults to the info (i) icon.
// `placement` — "top" (default) or "bottom".
// `compact`   — auto-width, single-line tooltip for short labels/titles.
// `variant`   — "info" (default, brand purple) or "error" (red; e.g. why a
//               knowledge row failed — see knowledgeStatus.js).
const InfoTooltip = ({ text, children, placement = "top", compact = false, variant = "info" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const showFrameRef = useRef(null);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      if (showFrameRef.current) {
        cancelAnimationFrame(showFrameRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (shouldRender && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 280;
      const padding = 12;
      const verticalGap = 10;
      const viewportWidth = window.innerWidth;

      // Center on the trigger by default.
      let left = rect.left + rect.width / 2;

      // Keep the wide info tooltip on-screen (skip for compact, which is
      // narrow and auto-sized).
      if (!compact) {
        if (left - tooltipWidth / 2 < padding) {
          left = padding + tooltipWidth / 2;
        }
        if (left + tooltipWidth / 2 > viewportWidth - padding) {
          left = viewportWidth - padding - tooltipWidth / 2;
        }
      }

      const top =
        placement === "bottom"
          ? rect.bottom + verticalGap
          : rect.top - verticalGap;

      setPosition({ top, left });
    }
  }, [shouldRender, placement, compact]);

  const showTooltip = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (showFrameRef.current) {
      cancelAnimationFrame(showFrameRef.current);
    }
    setShouldRender(true);
    setIsVisible(false);
    showFrameRef.current = requestAnimationFrame(() => {
      showFrameRef.current = requestAnimationFrame(() => {
        setIsVisible(true);
        showFrameRef.current = null;
      });
    });
  };

  const hideTooltip = () => {
    if (showFrameRef.current) {
      cancelAnimationFrame(showFrameRef.current);
      showFrameRef.current = null;
    }
    setIsVisible(false);
    hideTimerRef.current = setTimeout(() => {
      setShouldRender(false);
      hideTimerRef.current = null;
    }, 180);
  };

  return (
    <span
      className="info-tooltip-wrapper"
      ref={triggerRef}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children || (
        <FontAwesomeIcon className="info-tooltip-icon" icon={faCircleInfo} />
      )}
      {shouldRender &&
        createPortal(
          <span
            className={`info-tooltip${
              placement === "bottom" ? " info-tooltip-bottom" : ""
            }${compact ? " info-tooltip-compact" : ""}${
              variant === "error" ? " info-tooltip-error" : ""
            }${
              isVisible ? " info-tooltip-visible" : ""
            }`}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
          >
            {text}
          </span>,
          document.body
        )}
    </span>
  );
};

export default InfoTooltip;
